import type { Channel } from 'phoenix';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import useAppSelector from '../app/redux/useAppSelector';

let shouldDebugAiCopilot = false;

if (typeof window !== 'undefined' && window.__slab.DEVTOOLS) {
  window.__slab.DEVTOOLS.setDebugAiCopilot = (enabled: boolean) => {
    shouldDebugAiCopilot = enabled;
  };
}

const consoleInfo = (...args: unknown[]) => {
  if (shouldDebugAiCopilot) {
    // eslint-disable-next-line no-console
    console.info(...args);
  }
};

export type Source = {
  type: 'post';
  id: string;
  title: string;
};

interface UseAiAskOptions {
  onStart: (responseId: string) => void;
  onAnswerText: (responseId: string, text: string) => void;
  onFinish: (responseId: string, sources: Source[]) => void;
}

const useAiAsk = (options: UseAiAskOptions) => {
  const { onStart, onAnswerText, onFinish } = options;

  const onStartEvent = useEffectEvent(onStart);
  const onAnswerTextEvent = useEffectEvent(onAnswerText);
  const onFinishEvent = useEffectEvent(onFinish);

  const [isGenerating, setIsGenerating] = useState(false);
  const channelRef = useRef<Channel | null>(null);
  const previousResponseIdRef = useRef<string | null>(null);

  const socket = useAppSelector((state) => state.session.socket);

  useEffect(() => {
    if (!socket) return undefined;
    const channel = socket.channel('stream');

    channel.on('start', (payload) => {
      consoleInfo('start', payload);

      if (typeof payload.response_id === 'string') {
        onStartEvent(payload.response_id);
      }
    });

    channel.on('answer_text', (payload) => {
      consoleInfo('answer_text', payload);

      if (
        typeof payload.response_id === 'string' &&
        typeof payload.text === 'string'
      ) {
        onAnswerTextEvent(payload.response_id, payload.text);
      }
    });

    // TODO: Not used yet
    // channel.on('answer_delta', ({ delta }) => {
    //   consoleInfo('answer_delta', delta);
    // });

    channel.on('response', (payload) => {
      consoleInfo('response', payload);

      previousResponseIdRef.current = payload.response_id;

      if (typeof payload.response_id === 'string') {
        const sources = Array.isArray(payload.sources) ? payload.sources : [];

        onFinishEvent(
          payload.response_id,
          sources
            .map((source: unknown) => {
              if (
                typeof source === 'object' &&
                source !== null &&
                'id' in source &&
                typeof source.id === 'string' &&
                'primary' in source &&
                typeof source.primary === 'string'
              ) {
                return {
                  type: 'post',
                  id: source.id,
                  title: source.primary,
                };
              }

              return null;
            })
            .filter(Boolean),
        );
      }

      setIsGenerating(false);
    });

    channel.join();
    consoleInfo('joined');

    channelRef.current = channel;
    return () => {
      channel.leave();
      channelRef.current = null;
    };
  }, [socket]);

  const sendMessage = useCallback(
    (message: string): boolean => {
      const channel = channelRef.current;
      if (isGenerating || !channel) return false;

      const previousId = previousResponseIdRef.current;

      consoleInfo('sendMessage', message, previousId);
      channel.push(
        'ai_ask',
        previousId
          ? {
              input: message,
              previous_id: previousId,
            }
          : {
              input: message,
            },
      );
      setIsGenerating(true);

      return true;
    },
    [isGenerating],
  );

  return {
    sendMessage,
    isGenerating,
  };
};

export default useAiAsk;
